import React, { useEffect, useRef, useState } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import api from '../api';
import { useTheme } from '../contexts/ThemeContext';

const SAAT = (s: string) =>
  new Date(s).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });

interface TestMesaj { role: 'user' | 'ai'; icerik: string; zaman: string; }

export default function WhatsappAI() {
  usePageTitle('WhatsApp AI Asistan');
  const { isDark } = useTheme();
  const card      = isDark ? '#1a1a1a' : 'white';
  const border    = isDark ? '#2a2a2a' : '#eee';
  const textSec   = isDark ? '#999'    : '#888';
  const inputBg   = isDark ? '#111'    : '#fafafa';
  const chatBg    = isDark ? '#0d1117' : '#e5ddd5';

  const MODELLER = [
    { id: 'claude-haiku-4-5-20251001', ad: 'Claude Haiku 4.5', aciklama: 'Hızlı · Ekonomik · ~$5/ay', renk: '#22c55e' },
    { id: 'claude-sonnet-4-6',         ad: 'Claude Sonnet 4.6', aciklama: 'Dengeli · Güçlü · ~$18/ay', renk: '#f59e0b' },
    { id: 'claude-opus-4-8',           ad: 'Claude Opus 4.8',   aciklama: 'En güçlü · Pahalı · ~$80/ay', renk: '#ef4444' },
  ];

  // Prompt state
  const [prompt, setPrompt]             = useState('');
  const [model, setModel]               = useState('claude-haiku-4-5-20251001');
  const [aktif, setAktif]               = useState(true);
  const [yukleniyor, setYukleniyor]     = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [kayitMesaj, setKayitMesaj]     = useState('');

  // Test chat state
  const [testMesajlar, setTestMesajlar] = useState<TestMesaj[]>([]);
  const [testInput, setTestInput]       = useState('');
  const [testYukleniyor, setTestYukleniyor] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get('/api/whatsapp/ai-config')
      .then(r => { setPrompt(r.data.sistem_prompt || ''); setAktif(r.data.aktif ?? true); setModel(r.data.model || 'claude-haiku-4-5-20251001'); })
      .catch(() => {})
      .finally(() => setYukleniyor(false));
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [testMesajlar]);

  const kaydet = async () => {
    if (!prompt.trim()) return;
    setKaydediliyor(true); setKayitMesaj('');
    try {
      await api.put('/api/whatsapp/ai-config', { sistem_prompt: prompt, aktif, model });
      setKayitMesaj('✓ Kaydedildi');
      setTimeout(() => setKayitMesaj(''), 3000);
    } catch { setKayitMesaj('Kaydedilemedi'); }
    finally { setKaydediliyor(false); }
  };

  const testGonder = async () => {
    if (!testInput.trim() || testYukleniyor) return;
    const kullanici = testInput.trim();
    setTestInput('');
    const zaman = new Date().toISOString();
    setTestMesajlar(p => [...p, { role: 'user', icerik: kullanici, zaman }]);
    setTestYukleniyor(true);
    try {
      const r = await api.post('/api/whatsapp/test', { mesaj: kullanici, gecmis: testMesajlar });
      setTestMesajlar(p => [...p, { role: 'ai', icerik: r.data.yanit, zaman: new Date().toISOString() }]);
    } catch (err: any) {
      setTestMesajlar(p => [...p, { role: 'ai', icerik: `⚠️ Hata: ${err.response?.data?.hata || 'Yanıt alınamadı'}`, zaman: new Date().toISOString() }]);
    } finally { setTestYukleniyor(false); }
  };

  return (
    <div style={{ padding: '24px 24px 48px' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>💬 WhatsApp AI Asistan</h1>
        <div style={{ fontSize: 13, color: textSec, marginTop: 4 }}>
          Sistem promptu ve canlı test arayüzü
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: 20, alignItems: 'start' }}>

        {/* SOL: Prompt editörü */}
        <div>
          {/* Durum kartı */}
          <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: textSec, marginBottom: 4, fontWeight: 600 }}>DURUM</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: aktif ? '#22c55e' : '#ef4444' }} />
              <span style={{ fontSize: 13, fontWeight: 600 }}>{aktif ? 'Aktif — Otomatik yanıt açık' : 'Pasif — Otomatik yanıt kapalı'}</span>
            </div>
          </div>

          {/* Model seçici */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: textSec, fontWeight: 600, marginBottom: 8 }}>MODEL SEÇ</div>
            <div style={{ display: 'flex', gap: 8 }}>
              {MODELLER.map(m => (
                <button key={m.id} onClick={() => setModel(m.id)} style={{
                  flex: 1, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                  border: `2px solid ${model === m.id ? m.renk : border}`,
                  background: model === m.id ? (isDark ? `${m.renk}18` : `${m.renk}10`) : card,
                  transition: 'all 0.15s',
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: model === m.id ? m.renk : isDark ? '#e0e0e0' : '#111', marginBottom: 2 }}>
                    {m.ad}
                  </div>
                  <div style={{ fontSize: 10, color: textSec }}>{m.aciklama}</div>
                </button>
              ))}
            </div>
          </div>

          <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: 20 }}>
            {/* Aktif toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, paddingBottom: 14, borderBottom: `1px solid ${border}` }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>Otomatik Yanıt</div>
                <div style={{ fontSize: 11, color: textSec, marginTop: 2 }}>Kapalıyken mesajlar CRM'e düşer ama yanıt gönderilmez</div>
              </div>
              <button onClick={() => setAktif(p => !p)} style={{
                width: 46, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: aktif ? '#22c55e' : '#d1d5db', position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  position: 'absolute', top: 2, left: aktif ? 23 : 2,
                  width: 20, height: 20, borderRadius: '50%', background: 'white',
                  transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }} />
              </button>
            </div>

            {/* Sistem promptu */}
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Sistem Promptu</div>
            <div style={{ fontSize: 12, color: textSec, marginBottom: 10 }}>
              Şubeler, dersler, fiyatlar, kayıt süreci, SSS — AI bu bilgilere göre yanıt verir.
            </div>
            {yukleniyor ? (
              <div style={{ color: textSec, fontSize: 13 }}>Yükleniyor...</div>
            ) : (
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                rows={18}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: `1px solid ${border}`, background: inputBg,
                  color: isDark ? '#e0e0e0' : '#000', fontSize: 12.5,
                  lineHeight: 1.6, resize: 'vertical', boxSizing: 'border-box',
                  fontFamily: 'monospace', outline: 'none',
                }}
              />
            )}
            <div style={{ fontSize: 11, color: textSec, marginTop: 4, marginBottom: 14 }}>{prompt.length} karakter</div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
              {kayitMesaj && (
                <span style={{ fontSize: 13, color: kayitMesaj.startsWith('✓') ? '#22c55e' : '#ef4444', fontWeight: 500 }}>
                  {kayitMesaj}
                </span>
              )}
              <button onClick={kaydet} disabled={kaydediliyor || !prompt.trim()} style={{
                padding: '9px 22px', borderRadius: 8, border: 'none',
                background: prompt.trim() ? '#534AB7' : '#aaa', color: 'white',
                cursor: prompt.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600,
              }}>
                {kaydediliyor ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>

        {/* SAĞ: Test chat */}
        <div style={{ position: 'sticky', top: 24 }}>
          <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, overflow: 'hidden' }}>
            {/* Chat header */}
            <div style={{ background: isDark ? '#075E54' : '#128C7E', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17 }}>🤖</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>Tango Asistan</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>AI Test Modu</div>
              </div>
              <button
                onClick={() => setTestMesajlar([])}
                title="Sohbeti sıfırla"
                style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8,
                  color: 'white', cursor: 'pointer', padding: '5px 10px', fontSize: 12,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                ↺ Sıfırla
              </button>
            </div>

            {/* Mesajlar */}
            <div style={{ height: 420, overflowY: 'auto', padding: 12, background: chatBg, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {testMesajlar.length === 0 && (
                <div style={{ textAlign: 'center', marginTop: 60, color: isDark ? '#555' : '#aaa', fontSize: 13 }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>💬</div>
                  Mesaj yaz, AI nasıl yanıt verdiğini gör
                </div>
              )}
              {testMesajlar.map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '80%', padding: '8px 12px', borderRadius: m.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                    background: m.role === 'user' ? '#DCF8C6' : (isDark ? '#1e1e1e' : 'white'),
                    color: isDark && m.role !== 'user' ? '#e0e0e0' : '#111',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  }}>
                    {m.role === 'ai' && <div style={{ fontSize: 10, fontWeight: 700, color: '#128C7E', marginBottom: 3 }}>Tango Asistan</div>}
                    <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{m.icerik}</div>
                    <div style={{ fontSize: 10, color: '#aaa', textAlign: 'right', marginTop: 3 }}>
                      {SAAT(m.zaman)}{m.role === 'user' && ' ✓✓'}
                    </div>
                  </div>
                </div>
              ))}
              {testYukleniyor && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <div style={{ background: isDark ? '#1e1e1e' : 'white', padding: '10px 14px', borderRadius: '4px 16px 16px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {[0,1,2].map(i => (
                        <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: '#aaa', animation: `bounce 1.2s ${i*0.2}s infinite` }} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: `1px solid ${border}`, background: card }}>
              <input
                value={testInput}
                onChange={e => setTestInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); testGonder(); } }}
                placeholder="Test mesajı yaz..."
                style={{
                  flex: 1, padding: '9px 12px', borderRadius: 20, border: `1px solid ${border}`,
                  background: inputBg, color: isDark ? '#e0e0e0' : '#000', fontSize: 13, outline: 'none',
                }}
              />
              <button onClick={testGonder} disabled={!testInput.trim() || testYukleniyor} style={{
                width: 38, height: 38, borderRadius: '50%', border: 'none',
                background: testInput.trim() ? '#128C7E' : '#ccc', color: 'white',
                cursor: testInput.trim() ? 'pointer' : 'not-allowed', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                ➤
              </button>
            </div>
          </div>

          {false && (
            <button onClick={() => setTestMesajlar([])} style={{
              width: '100%', marginTop: 8, padding: '7px', borderRadius: 8,
              border: `1px solid ${border}`, background: 'transparent', color: textSec,
              cursor: 'pointer', fontSize: 12,
            }}>
              Sohbeti temizle
            </button>
          )}
        </div>
      </div>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
}
